import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { FirebaseContext } from "../contexts/FirebaseContext";
import TopBar from "../components/TopBar";
import accountIcon from "../assets/icon_account.svg";
import settingsIcon from "../assets/icon_settings.svg";
import Colors from "../constants/Colors";
import { clientForInvoice, buildInvoicePdfData } from "../utils/invoiceData";
import { generateInvoicePdf } from "../utils/invoicePdf";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function fmtDate(ms) {
  if (!ms) return "-";
  const d = new Date(ms);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

function InvoiceListScreen() {
  const navigate = useNavigate();
  const {
    invoices,
    clients,
    projects,
    createInvoice,
    duplicateLastInvoice,
    updateInvoice,
    getInvoiceSourceData,
  } = useContext(FirebaseContext);

  const handleNew = async () => {
    const docId = await createInvoice();
    if (docId) navigate(`/invoiceDetail/${docId}`);
  };

  const handleDuplicate = async () => {
    const docId = await duplicateLastInvoice();
    if (docId) navigate(`/invoiceDetail/${docId}`);
  };

  const handleDownload = async (e, invoice) => {
    e.stopPropagation();
    const { tasks, timeRecords } = await getInvoiceSourceData();
    generateInvoicePdf(buildInvoicePdfData(invoice, { clients, tasks, projects, timeRecords }));
  };

  const togglePaid = (e, invoice) => {
    e.stopPropagation();
    updateInvoice(invoice.id, { paid: !invoice.paid });
  };

  return (
    <div style={styles.container}>
      <TopBar
        leftContainer={<span style={styles.title}>Invoices</span>}
        rightContainer={
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <button style={styles.iconBtn} onClick={() => navigate("/clients")} title="Clients" aria-label="Clients">
              <img src={accountIcon} alt="" style={styles.icon} />
            </button>
            <button style={styles.iconBtn} onClick={() => navigate("/invoiceSettings")} title="Invoice settings" aria-label="Invoice settings">
              <img src={settingsIcon} alt="" style={styles.icon} />
            </button>
          </div>
        }
      />
      <div style={styles.body}>
        {invoices.length === 0 && <div style={styles.empty}>No invoices yet.</div>}
        {invoices.map((inv) => {
          const client = clientForInvoice(inv, clients);
          return (
            <div key={inv.id} style={styles.row} onClick={() => navigate(`/invoiceDetail/${inv.id}`)}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.rowTitle}>
                  #{inv.number} · {client ? client.name || "(no name)" : "No client"}
                </div>
                <div style={styles.rowSub}>{fmtDate(inv.issueDate)}</div>
              </div>
              <button
                style={{ ...styles.badge, ...(inv.paid ? styles.badgePaid : styles.badgeUnpaid) }}
                onClick={(e) => togglePaid(e, inv)}
              >
                {inv.paid ? "Paid" : "Unpaid"}
              </button>
              <button style={styles.pdfBtn} onClick={(e) => handleDownload(e, inv)} title="Download PDF">
                PDF
              </button>
            </div>
          );
        })}
      </div>

      <div style={styles.ctaBar}>
        {invoices.length > 0 && (
          <button style={styles.secondaryCta} onClick={handleDuplicate}>
            Duplicate Last
          </button>
        )}
        <button style={styles.primaryCta} onClick={handleNew}>
          New Invoice
        </button>
      </div>
    </div>
  );
}

const styles = {
  container: { paddingTop: "66px", height: "calc(100vh - 60px)", boxSizing: "border-box" },
  title: { fontWeight: 700, fontSize: "18px", color: "var(--text)" },
  iconBtn: { background: "none", border: "none", cursor: "pointer", padding: "4px", display: "flex" },
  icon: { width: "22px", height: "22px", filter: "var(--icon-filter)" },
  body: { padding: "16px", paddingBottom: "96px", height: "100%", overflowY: "auto", boxSizing: "border-box" },
  empty: { color: "var(--text-muted)", fontSize: "14px" },
  row: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "12px 14px",
    marginBottom: "8px",
    borderRadius: "10px",
    background: "var(--card)",
    cursor: "pointer",
  },
  rowTitle: { color: "var(--text)", fontSize: "15px", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  rowSub: { color: "var(--text-muted)", fontSize: "12px", marginTop: "2px" },
  badge: { border: "none", borderRadius: "999px", padding: "4px 12px", fontSize: "12px", fontWeight: 700, cursor: "pointer" },
  badgePaid: { background: "#2e9e5b", color: "#fff" },
  badgeUnpaid: { background: "var(--surface)", color: "var(--text-muted)", border: "1px solid var(--border)" },
  pdfBtn: { background: "none", border: "1px solid var(--primary)", color: "var(--primary)", borderRadius: "8px", padding: "5px 10px", fontSize: "12px", fontWeight: 700, cursor: "pointer" },
  ctaBar: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: "60px",
    display: "flex",
    gap: "10px",
    padding: "12px 16px",
    background: "var(--bg)",
    borderTop: "1px solid var(--border)",
    boxSizing: "border-box",
  },
  primaryCta: { flex: 1, padding: "12px", background: Colors.primary, color: "#fff", border: "none", borderRadius: "10px", fontWeight: 700, cursor: "pointer" },
  secondaryCta: { flex: 1, padding: "12px", background: "var(--surface)", color: "var(--text)", border: "1px solid var(--border)", borderRadius: "10px", fontWeight: 700, cursor: "pointer" },
};

export default InvoiceListScreen;
